import { type RefObject, useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import {
  BAND_ROTATE_MS,
  bandCookie,
  bandKeys,
  parseMinimized,
  readBandCookie,
  shouldStayMinimized,
} from '@arthelper/shared/bounties/band-state';

/**
 * Which bounty the band shows, and whether it is minimized (#4600).
 *
 * It turns to the next bounty every 10s, so a member who stays on one page sees all of
 * them. It holds still while `paused` (hovered, focused, a panel open), while the tab is
 * hidden, and under reduced motion. Tapping the arrow restarts the clock.
 *
 * Minimized is read from the shared cookie and holds while every bounty on offer is one
 * she minimized over. A new one reopens the band and clears the cookie, and `onReopen` runs.
 *
 * Counterpart of town-square's `components/bounty/useBandRotation.ts` — edit both or
 * neither.
 */
export function useBandRotation(
  bounties: ReadonlyArray<{ id: string; periodKey: string }>,
  { paused, onReopen }: { paused: boolean; onReopen: () => void }
) {
  const keys = bandKeys(bounties);
  const signature = keys.join('|');
  const count = keys.length;
  const [saved, setSaved] = useState<string[] | null>(null);
  const [index, setIndex] = useState(0);
  const [restart, setRestart] = useState(0);
  const latest = useRef(keys);
  latest.current = keys;

  // Read before the band first paints, so a minimized band never flashes open.
  useLayoutEffect(() => {
    setSaved(parseMinimized(readBandCookie(document.cookie)));
  }, []);

  const minimized = shouldStayMinimized(saved, keys);

  useEffect(() => {
    if (!saved || count === 0 || minimized) return;
    document.cookie = bandCookie(null, window.location.hostname);
    setSaved(null);
    onReopen();
    // `signature` stands in for `keys`: a fresh array every render.
  }, [saved, signature, count, minimized, onReopen]);

  useEffect(() => {
    if (minimized || paused || count < 2) return;
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
    const id = window.setInterval(() => {
      if (document.visibilityState === 'visible') setIndex((current) => current + 1);
    }, BAND_ROTATE_MS);
    return () => window.clearInterval(id);
  }, [minimized, paused, count, restart]);

  const next = useCallback(() => {
    setIndex((current) => current + 1);
    setRestart((current) => current + 1);
  }, []);

  const minimize = useCallback(() => {
    document.cookie = bandCookie(latest.current, window.location.hostname);
    setSaved(latest.current);
  }, []);

  const expand = useCallback(() => {
    document.cookie = bandCookie(null, window.location.hostname);
    setSaved(null);
  }, []);

  return { index: count ? index % count : 0, count, next, minimized, minimize, expand };
}

/**
 * THE SLOT REEL (#4600, Nick, 27 Oct): when the band turns to another bounty, the one leaving
 * rolls up and out, the next rolls up from below with a small overshoot, and the next
 * button's arrows spin. Never on first paint, and nothing under reduced motion.
 *
 * React has already swapped the strip's content by the time this runs, so the bounty
 * leaving is a copy taken on the previous render, laid over the strip while it rolls away.
 */
export function useReel(wrapRef: RefObject<HTMLElement | null>, id: string | null) {
  const shown = useRef<string | null>(null);
  const ghost = useRef<HTMLElement | null>(null);
  useEffect(() => {
    const wrap = wrapRef.current;
    const strip = wrap?.querySelector<HTMLElement>('[data-testid="bounty-strip"]');
    const previous = shown.current;
    shown.current = id;
    const leaving = ghost.current;
    ghost.current = strip ? (strip.cloneNode(true) as HTMLElement) : null;
    if (!wrap || !strip || !leaving || !previous || !id || previous === id || !strip.animate) return;
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
    roll(wrap, strip, leaving);
  });
}

function roll(wrap: HTMLElement, strip: HTMLElement, leaving: HTMLElement) {
  wrap.style.position = 'relative';
  wrap.style.overflow = 'clip';
  leaving.removeAttribute('data-testid');
  leaving.dataset.reelGhost = '';
  leaving.setAttribute('aria-hidden', 'true');
  leaving.inert = true; // the ghost's copied buttons must not take focus or clicks
  leaving.querySelectorAll('[data-testid]').forEach((el) => el.removeAttribute('data-testid'));
  // The copy keeps the 🎯's hello class, which would replay its wiggle on the way out.
  leaving.querySelector('.is-hello')?.classList.remove('is-hello');
  Object.assign(leaving.style, {
    position: 'absolute',
    top: `${strip.offsetTop}px`,
    left: `${strip.offsetLeft}px`,
    width: `${strip.offsetWidth}px`,
    height: `${strip.offsetHeight}px`,
    margin: '0',
    pointerEvents: 'none',
  });
  wrap.appendChild(leaving);
  leaving.animate([{ transform: 'translateY(0)' }, { transform: 'translateY(-115%)' }], {
    duration: 260,
    easing: 'ease-in',
    fill: 'forwards',
  });
  const arriving = strip.animate(
    [
      { transform: 'translateY(115%)' },
      { transform: 'translateY(-10%)', offset: 0.75 },
      { transform: 'translateY(0)' },
    ],
    { duration: 520, easing: 'ease-out' }
  );
  strip
    .querySelector('.bounty-strip-next svg')
    ?.animate([{ transform: 'rotate(0)' }, { transform: 'rotate(360deg)' }], { duration: 480, easing: 'ease-out' });
  arriving.onfinish = () => {
    leaving.remove();
    // A turn that started mid-roll still needs the clip; the last one to finish lifts it.
    if (wrap.querySelector(':scope > [data-reel-ghost]')) return;
    wrap.style.position = '';
    wrap.style.overflow = '';
  };
}
